import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react"
import { ChevronDown, ChevronRight, Container, MapPin } from "lucide-react"
import { DriverDisplayInfo } from "@/reducers/accountChatsSelectors"
import { TrailerLocation } from "@/reducers/trailerLocationsSlice"
import { Button } from "@/components/ui/button"
import { DriverCard } from "@/components/fleetOverview/driver-card"
import DriverInfo from "@/components/fleetOverview/driver-info"
import { useFeatureFlagEnabled } from "posthog-js/react"
import {
  HIDE_OFF_ROUTE_STATUS_BADGES_FLAG,
  shouldShowReferenceRouteStatusBadge,
} from "@/lib/referenceRouteStatusBadge"
import { useTranslation } from "react-i18next"
import { getIntlLocale } from "@/lib/i18n/format"

type DriversListProps = {
  drivers: DriverDisplayInfo[]
  // Trailers have no chat behind them: a row only opens the popup on the
  // map or flies there. Already filtered by the search box and the switch.
  trailers?: TrailerLocation[]
  onSelectTrailer?: (trailer: TrailerLocation) => void
  onFlyToTrailer?: (trailer: TrailerLocation) => void
  onSelectDriver: (driver: DriverDisplayInfo) => void
  onFlyToDriver: (driver: DriverDisplayInfo) => void
  onNavigateToChat: (jid: string) => void
  selectedDriver: DriverDisplayInfo | null
  hoveredDriver: string | null
  onHoverDriver: (jid: string | null) => void
  /** Who dispatches a vehicle, for a fleet manager's cards. */
  dispatcherLabelOf?: (driver: DriverDisplayInfo) => string | undefined
}

export type DriversListRef = {
  scrollToDriver: (jid: string) => void
}

type FleetGroup = "drivers" | "trailers"

const COLLAPSED_GROUPS_STORAGE_KEY = "fleetOverviewCollapsedGroups"

const loadCollapsedGroups = (): Set<FleetGroup> => {
  try {
    const stored = localStorage.getItem(COLLAPSED_GROUPS_STORAGE_KEY)
    if (stored) return new Set(JSON.parse(stored) as FleetGroup[])
  } catch (error) {
    console.warn("Failed to load collapsed fleet groups:", error)
  }
  return new Set()
}

const saveCollapsedGroups = (groups: Set<FleetGroup>) => {
  try {
    localStorage.setItem(
      COLLAPSED_GROUPS_STORAGE_KEY,
      JSON.stringify(Array.from(groups)),
    )
  } catch (error) {
    console.warn("Failed to save collapsed fleet groups:", error)
  }
}

function GroupHeader({
  label,
  count,
  collapsed,
  onToggle,
}: {
  label: string
  count: number
  collapsed: boolean
  onToggle: () => void
}) {
  return (
    <div
      className="flex cursor-pointer select-none flex-row items-center gap-1 pb-3 pl-4 pr-3 text-xs font-medium"
      onClick={onToggle}
    >
      <div className="flex-grow">
        {label} ({count})
      </div>
      {collapsed ? (
        <ChevronRight className="h-4 w-4" />
      ) : (
        <ChevronDown className="h-4 w-4" />
      )}
    </div>
  )
}

const DriversList = forwardRef<DriversListRef, DriversListProps>(
  (
    {
      drivers,
      trailers = [],
      onSelectTrailer,
      onFlyToTrailer,
      onSelectDriver,
      onFlyToDriver,
      onNavigateToChat,
      selectedDriver,
      hoveredDriver,
      onHoverDriver,
      dispatcherLabelOf,
    },
    ref,
  ) => {
    const { t } = useTranslation()
    const driverRefs = useRef<Record<string, HTMLDivElement | null>>({})
    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const hideOffRouteStatusBadges = Boolean(
      useFeatureFlagEnabled(HIDE_OFF_ROUTE_STATUS_BADGES_FLAG),
    )

    const [collapsedGroups, setCollapsedGroups] =
      useState<Set<FleetGroup>>(loadCollapsedGroups)
    const driversCollapsed = collapsedGroups.has("drivers")
    const trailersCollapsed = collapsedGroups.has("trailers")

    const toggleGroup = useCallback((group: FleetGroup) => {
      setCollapsedGroups((previous) => {
        const next = new Set(previous)
        if (next.has(group)) next.delete(group)
        else next.add(group)
        saveCollapsedGroups(next)
        return next
      })
    }, [])

    const scrollTo = useCallback((jid: string) => {
      const driverElement = driverRefs.current[jid]
      const scrollContainer = scrollContainerRef.current

      if (driverElement && scrollContainer) {
        const containerRect = scrollContainer.getBoundingClientRect()
        const elementRect = driverElement.getBoundingClientRect()

        // Check if element is already visible
        const isVisible =
          elementRect.top >= containerRect.top &&
          elementRect.bottom <= containerRect.bottom

        if (!isVisible) {
          driverElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          })
        }
      }
    }, [])

    // A driver picked on the map while the group is collapsed: expand it
    // first and scroll once the cards are back in the DOM.
    const pendingScrollJidRef = useRef<string | null>(null)
    useEffect(() => {
      const jid = pendingScrollJidRef.current
      if (jid && !driversCollapsed) {
        pendingScrollJidRef.current = null
        scrollTo(jid)
      }
    }, [driversCollapsed, scrollTo])

    useImperativeHandle(
      ref,
      () => ({
        scrollToDriver: (jid: string) => {
          if (driversCollapsed) {
            pendingScrollJidRef.current = jid
            toggleGroup("drivers")
            return
          }
          scrollTo(jid)
        },
      }),
      [driversCollapsed, scrollTo, toggleGroup],
    )

    return (
      <div className="scrollbar-gutter-stable flex h-full flex-col">
        <div className="flex h-full flex-col overflow-hidden">
          {drivers.length > 0 && (
            <GroupHeader
              label={t("fleet.fleetOverview.drivers")}
              count={drivers.length}
              collapsed={driversCollapsed}
              onToggle={() => toggleGroup("drivers")}
            />
          )}
          <div
            ref={scrollContainerRef}
            className="flex-1 overflow-y-auto overflow-x-hidden"
          >
            {drivers.length > 0 ? (
              !driversCollapsed &&
              drivers.map((driver) => (
                <div
                  key={driver.jid}
                  ref={(el) => {
                    driverRefs.current[driver.jid] = el
                  }}
                  className="relative mx-2 cursor-pointer transition-colors duration-200 hover:rounded-md hover:bg-accent hover:text-accent-foreground"
                  onMouseEnter={() => onHoverDriver(driver.jid)}
                  onMouseLeave={() => onHoverDriver(null)}
                  onClick={() => onSelectDriver(driver)}
                >
                  <DriverCardContainer
                    driver={driver}
                    canShowPopover={
                      !selectedDriver && hoveredDriver === driver.jid
                    }
                    isActive={selectedDriver?.jid === driver.jid}
                    isHovered={hoveredDriver === driver.jid}
                    onSelectDriver={() => onSelectDriver(driver)}
                    onFlyToDriver={() => onFlyToDriver(driver)}
                    onNavigateToChat={onNavigateToChat}
                    hideOffRouteStatusBadges={hideOffRouteStatusBadges}
                    dispatcherLabel={dispatcherLabelOf?.(driver)}
                  />
                </div>
              ))
            ) : trailers.length === 0 ? (
              <div className="p-5 text-center text-base font-medium">
                {t("fleet.fleetOverview.noDrivers")}
              </div>
            ) : null}
            {trailers.length > 0 && (
              <div className={drivers.length > 0 ? "pt-3" : ""}>
                <GroupHeader
                  label={t("fleet.fleetOverview.trailers")}
                  count={trailers.length}
                  collapsed={trailersCollapsed}
                  onToggle={() => toggleGroup("trailers")}
                />
                {!trailersCollapsed &&
                  trailers.map((trailer) => (
                    <TrailerRow
                      key={trailer.id}
                      trailer={trailer}
                      onSelect={onSelectTrailer}
                      onFlyTo={onFlyToTrailer}
                    />
                  ))}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  },
)

function TrailerRow({
  trailer,
  onSelect,
  onFlyTo,
}: {
  trailer: TrailerLocation
  onSelect?: (trailer: TrailerLocation) => void
  onFlyTo?: (trailer: TrailerLocation) => void
}) {
  const { t } = useTranslation()
  const odometer = trailer.location?.odometer
  return (
    <div
      className="group mx-2 flex cursor-pointer items-center gap-3 rounded-md py-2 pl-4 pr-2 hover:bg-sidebar-accent"
      onClick={() => onSelect?.(trailer)}
    >
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
        <Container className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate font-semibold text-gray-900 dark:text-gray-100">
          {trailer.label || t("fleet.fleetOverview.unnamedTrailer")}
        </div>
        {typeof odometer === "number" && (
          <div className="text-xs text-muted-foreground">
            {Math.round(odometer).toLocaleString(getIntlLocale())} km
          </div>
        )}
      </div>
      <Button
        variant="outline"
        size="sm"
        className="h-8 w-8 p-0 opacity-0 transition-opacity hover:bg-sidebar-accent group-hover:opacity-100"
        onClick={(event) => {
          event.stopPropagation()
          onFlyTo?.(trailer)
        }}
      >
        <MapPin className="h-4 w-4" />
      </Button>
    </div>
  )
}

function DriverCardContainer({
  driver,
  isActive,
  isHovered,
  onSelectDriver,
  onFlyToDriver,
  onNavigateToChat,
  canShowPopover = true,
  hideOffRouteStatusBadges,
  dispatcherLabel,
}: {
  driver: DriverDisplayInfo
  isActive: boolean
  isHovered: boolean
  onSelectDriver: () => void
  onFlyToDriver: () => void
  onNavigateToChat: (jid: string) => void
  canShowPopover?: boolean
  hideOffRouteStatusBadges: boolean
  dispatcherLabel?: string
}) {
  // Memoize popover content to prevent unnecessary re-renders
  const popoverContent = useMemo(
    () => (driver.nextStop ? <DriverInfo {...driver} /> : undefined),
    [driver],
  )
  const showRouteStatusBadge = shouldShowReferenceRouteStatusBadge({
    isTracked: driver.isTracked,
    isOffRoute: driver.isOffRoute,
    hideOffRouteStatusBadges,
  })

  return (
    <DriverCard
      isActive={isActive}
      isHovered={isHovered}
      onSelectDriver={onSelectDriver}
      onFlyToDriver={onFlyToDriver}
      onNavigateToChat={onNavigateToChat}
      popoverContent={canShowPopover ? popoverContent : undefined}
      showRouteStatusBadge={showRouteStatusBadge}
      stationarySince={driver.location.stationarySince}
      dispatcherLabel={dispatcherLabel}
      {...driver}
    />
  )
}

DriversList.displayName = "DriverList"
export default DriversList
